import React, { useRef, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Download, X, Share2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import StreakShareCard from './StreakShareCard';

export default function StreakShareModal({ streak, gradeId, userName, language, onClose }) {
  const cardRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const fr = language === 'fr';

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#0A0A0A',
      });
      const link = document.createElement('a');
      link.download = `yumix-streak-${streak}j.png`;
      link.href = dataUrl;
      link.click();
      toast.success(fr ? 'Image telechargee !' : 'Image downloaded!');
    } catch (err) {
      console.error('Share image error:', err);
      toast.error(fr ? 'Erreur de generation' : 'Generation error');
    } finally {
      setGenerating(false);
    }
  }, [streak, fr]);

  const handleNativeShare = useCallback(async () => {
    if (!cardRef.current) return;
    setGenerating(true);
    try {
      const { toBlob } = await import('html-to-image');
      const blob = await toBlob(cardRef.current, {
        quality: 0.95,
        pixelRatio: 2,
        backgroundColor: '#0A0A0A',
      });
      if (blob && navigator.share) {
        const file = new File([blob], `yumix-streak-${streak}j.png`, { type: 'image/png' });
        await navigator.share({
          title: `YUMIX - ${streak} jours de streak!`,
          files: [file],
        });
      } else {
        // Fallback to download
        handleDownload();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        handleDownload();
      }
    } finally {
      setGenerating(false);
    }
  }, [streak, handleDownload]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="streak-share-modal">
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-md animate-in zoom-in-95 fade-in duration-500">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-card border border-border flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
          data-testid="close-share-modal"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Card preview */}
        <div className="flex justify-center mb-4">
          <div style={{ transform: 'scale(0.82)', transformOrigin: 'top center' }}>
            <StreakShareCard
              ref={cardRef}
              streak={streak}
              gradeId={gradeId}
              userName={userName}
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 px-4">
          {navigator.share && (
            <Button
              onClick={handleNativeShare}
              disabled={generating}
              className="flex-1 h-12 font-heading text-sm uppercase tracking-wider bg-primary text-primary-foreground"
              data-testid="share-streak-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Share2 className="w-4 h-4 mr-2" />}
              {fr ? 'Partager' : 'Share'}
            </Button>
          )}
          <Button
            onClick={handleDownload}
            disabled={generating}
            variant="outline"
            className="flex-1 h-12 font-heading text-sm uppercase tracking-wider border-border"
            data-testid="download-streak-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {fr ? 'Telecharger' : 'Download'}
          </Button>
        </div>
      </div>
    </div>
  );
}
